import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import NavigationHeader from '@/components/NavigationHeader';
import Footer from '@/components/Footer';
import ServiceRating from '@/components/ServiceRating';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Star, Phone, MapPin, Calendar, Clock, Building, MoveRight } from 'lucide-react';

interface Service {
  id: string;
  service_name: string;
  description: string;
  price: number;
  category: string;
  average_rating: number;
  total_ratings: number;
  service_providers: {
    company_name: string;
    phone: string;
    company_logo_url: string;
  } | null;
}

const ServicesPage = () => {
  const { toast } = useToast();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const [bookingData, setBookingData] = useState({
    customer_name: '',
    customer_phone: '',
    property_address: '',
    booking_date: '',
    booking_time: '',
    notes: ''
  });

  useEffect(() => {
    fetchServices();
  }, [selectedCategory]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('services')
        .select(`
          id,
          service_name,
          description,
          price,
          category,
          average_rating,
          total_ratings,
          is_active,
          provider_id,
          service_providers!inner (
            company_name,
            phone,
            company_logo_url
          )
        `)
        .eq('is_active', true)
        .order('average_rating', { ascending: false });

      if (error) {
        console.error('Error fetching services:', error);
        throw error;
      }
      
      console.log('Services data:', data);
      
      // Filter by category if needed
      let filteredData = data || [];
      if (selectedCategory !== 'all') {
        filteredData = filteredData.filter(service => service.category === selectedCategory);
      }
      
      setServices(filteredData);
    } catch (error: any) {
      console.error('Error fetching services:', error);
      toast({
        title: "خطأ",
        description: error.message || "حدث خطأ في تحميل الخدمات",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "يجب تسجيل الدخول",
          description: "يرجى تسجيل الدخول لحجز الخدمة",
          variant: "destructive"
        });
        return;
      }

      // Get tenant_id if exists
      const { data: tenantData } = await supabase
        .from('tenants')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      const { error } = await supabase
        .from('service_bookings')
        .insert({
          service_id: selectedService.id,
          user_id: user.id,
          tenant_id: tenantData?.id || null,
          customer_name: bookingData.customer_name,
          customer_phone: bookingData.customer_phone,
          property_address: bookingData.property_address,
          booking_date: bookingData.booking_date,
          booking_time: bookingData.booking_time,
          notes: bookingData.notes,
          status: 'pending'
        });

      if (error) throw error;

      toast({
        title: "تم إرسال الطلب بنجاح",
        description: "سيتم التواصل معك قريباً من قبل مزود الخدمة",
      });

      setIsBookingOpen(false);
      setBookingData({
        customer_name: '',
        customer_phone: '',
        property_address: '',
        booking_date: '',
        booking_time: '',
        notes: ''
      });
    } catch (error: any) {
      toast({
        title: "خطأ",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      cleaning: 'تنظيف',
      maintenance: 'صيانة',
      ac: 'تكييف',
      plumbing: 'سباكة',
      electrical: 'كهرباء',
      painting: 'دهان',
      other: 'أخرى'
    };
    return labels[category] || category;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <NavigationHeader />
      
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">خدمات العقارات</h1>
          <p className="text-lg text-gray-600">اختر الخدمة المناسبة لعقارك</p>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full md:w-64 mx-auto">
              <SelectValue placeholder="اختر التصنيف" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">جميع الخدمات</SelectItem>
              <SelectItem value="cleaning">تنظيف</SelectItem>
              <SelectItem value="maintenance">صيانة</SelectItem>
              <SelectItem value="ac">تكييف</SelectItem>
              <SelectItem value="plumbing">سباكة</SelectItem>
              <SelectItem value="electrical">كهرباء</SelectItem>
              <SelectItem value="painting">دهان</SelectItem>
              <SelectItem value="other">أخرى</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Services Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">جاري التحميل...</p>
          </div>
        ) : services.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-muted-foreground">لا توجد خدمات متاحة حالياً</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Card 
                key={service.id} 
                className="overflow-hidden transition-all duration-300 hover:shadow-2xl border border-gray-200 rounded-2xl bg-gradient-to-br from-white via-white to-gray-50 group"
              >
                  {/* صورة الشركة مع تأثيرات */}
                <div className="relative h-48 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center overflow-hidden">
                  {service.service_providers?.company_logo_url ? (
                    <img 
                      src={service.service_providers.company_logo_url} 
                      alt={service.service_providers?.company_name || 'شعار الشركة'}
                      className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                    />
                  ) : (
                    <Building className="h-20 w-20 text-blue-300" />
                  )}
                  
                  {/* طبقة تدرج فوق الصورة */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  
                  {/* التصنيف في الأعلى */}
                  <div className="absolute top-4 left-4 z-10">
                    <Badge className="bg-white/90 backdrop-blur-sm text-gray-800 border-0 py-1.5 px-3 rounded-full font-medium">
                      {getCategoryLabel(service.category)}
                    </Badge>
                  </div>
                  
                  {/* السعر في الأسفل */}
                  <div className="absolute bottom-4 left-4 text-white z-10">
                    <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 px-3 py-1 rounded-lg shadow-lg">
                      {service.price} ر.ع
                    </div>
                  </div>
                </div>

                <CardContent className="p-5">
                  {/* اسم الخدمة واسم الشركة */}
                  <div className="mb-3">
                    <h3 className="font-bold text-xl text-gray-900 mb-1 line-clamp-1 text-right hover:text-blue-600 transition-colors">
                      {service.service_name}
                    </h3>
                    <div className="flex items-center gap-1 text-gray-600 text-sm">
                      <Building className="h-3.5 w-3.5 text-blue-500" />
                      <span>{service.service_providers?.company_name || 'غير متوفر'}</span>
                    </div>
                  </div>

                  {/* التقييم */}
                  <div className="flex items-center gap-1 mb-3 bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-2 w-fit">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-bold text-gray-900">{service.average_rating || 0}</span>
                    <span className="text-sm text-gray-600">({service.total_ratings} تقييم)</span>
                  </div>

                  {/* الوصف */}
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2 text-right">
                    {service.description}
                  </p>

                  {/* معلومات الاتصال */}
                  <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-2 mb-4">
                    <Phone className="h-4 w-4 text-green-600" />
                    <span>{service.service_providers?.phone || 'غير متوفر'}</span>
                  </div>

                  {/* الأزرار */}
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 group"
                      onClick={() => {
                        setSelectedService(service);
                        setIsBookingOpen(true);
                      }}
                    >
                      <span className="flex items-center justify-center w-full">
                        <span>احجز الآن</span>
                        <MoveRight className="h-4 w-4 mr-2 transform group-hover:translate-x-1 transition-transform" />
                      </span>
                    </Button>
                    <ServiceRating 
                      serviceId={service.id}
                      serviceName={service.service_name}
                      onSuccess={fetchServices}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Booking Dialog */}
      <Dialog open={isBookingOpen} onOpenChange={setIsBookingOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>حجز خدمة: {selectedService?.service_name}</DialogTitle>
            <DialogDescription>
              املأ البيانات التالية لإتمام الحجز
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBookingSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer_name">الاسم الكامل</Label>
                <Input
                  id="customer_name"
                  value={bookingData.customer_name}
                  onChange={(e) => setBookingData({ ...bookingData, customer_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_phone">رقم الهاتف</Label>
                <Input
                  id="customer_phone"
                  type="tel"
                  value={bookingData.customer_phone}
                  onChange={(e) => setBookingData({ ...bookingData, customer_phone: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="property_address">عنوان العقار (مفصل)</Label>
              <Textarea
                id="property_address"
                value={bookingData.property_address}
                onChange={(e) => setBookingData({ ...bookingData, property_address: e.target.value })}
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="booking_date">التاريخ</Label>
                <Input
                  id="booking_date"
                  type="date"
                  value={bookingData.booking_date}
                  onChange={(e) => setBookingData({ ...bookingData, booking_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="booking_time">الوقت</Label>
                <Input
                  id="booking_time"
                  type="time"
                  value={bookingData.booking_time}
                  onChange={(e) => setBookingData({ ...bookingData, booking_time: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">ملاحظات (اختياري)</Label>
              <Textarea
                id="notes"
                value={bookingData.notes}
                onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                rows={3}
                placeholder="أي ملاحظات أو تفاصيل إضافية..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1">
                تأكيد الحجز
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsBookingOpen(false)}>
                إلغاء
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default ServicesPage;
